import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import { Card, CardContent } from '../components/ui/card';
import { Quote, Play, Heart, Users } from 'lucide-react';
import { usePageTracking } from '../hooks/useAnalytics';

const TestimonialsPage = () => {
    // Track page view
    usePageTracking('Testimonials');

    const testimonials = [
        {
            id: 1,
            videoUrl: "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/oxfjql6f_1.mov",
            name: "Recovery Room Brother",
            title: "Member Testimony"
        },
        {
            id: 2,
            videoUrl: "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/rh9b69n6_2.mov",
            name: "Recovery Room Brother",
            title: "Member Testimony"
        }
    ];

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            <main className="pt-32 pb-24 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            Who We Are
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Playfair_Display'] mb-6">
                            What Brothers Are Saying
                        </h1>
                        <div className="max-w-3xl mx-auto">
                            <p className="text-[#A0A0A0] text-lg leading-relaxed">
                                The most powerful stories come from those who have walked through the doors of the Recovery Room. 
                                Hear directly from our brothers as they share their experiences, breakthroughs, and the 
                                transformative power of community. These are real voices, real journeys, and real testimonies 
                                of what happens when Black men find a space to heal together.
                            </p>
                        </div>
                    </div>

                    {/* Quote Banner */}
                    <div className="mb-16">
                        <Card className="bg-gradient-to-r from-[#C9A227]/20 via-[#C9A227]/10 to-[#C9A227]/20 border-[#C9A227]/30">
                            <CardContent className="p-8 text-center">
                                <Quote className="w-10 h-10 text-[#C9A227] mx-auto mb-4 opacity-50" />
                                <p className="text-xl lg:text-2xl text-white font-['Playfair_Display'] italic">
                                    "In this room, we don't just talk—we transform."
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Video Testimonials Grid */}
                    <section className="mb-20">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-center">
                            {testimonials.map((testimony, idx) => (
                                <Card 
                                    key={testimony.id} 
                                    className="bg-[#4A5240] border-[#5A6350] overflow-hidden group hover:border-[#C9A227]/50 transition-all duration-300 w-full max-w-sm"
                                >
                                    <div className="relative">
                                        {/* Video Player - portrait aspect ratio for vertical videos */}
                                        <video 
                                            className="w-full aspect-[9/16] object-contain bg-black"
                                            controls
                                            preload="metadata"
                                            playsInline
                                        >
                                            <source src={testimony.videoUrl} type="video/quicktime" />
                                            <source src={testimony.videoUrl} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                        
                                        {/* Testimony Number Badge */}
                                        <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-[#C9A227] flex items-center justify-center text-[#3D4435] font-bold shadow-lg z-10">
                                            {idx + 1}
                                        </div>
                                    </div>
                                    
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                                                <Users className="w-6 h-6 text-[#C9A227]" />
                                            </div>
                                            <div>
                                                <h3 className="text-white font-semibold">{testimony.name}</h3>
                                                <p className="text-[#A0A0A0] text-sm">{testimony.title}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Impact Stats */}
                    <section className="mb-20">
                        <Card className="bg-[#4A5240] border-[#5A6350]">
                            <CardContent className="p-8 lg:p-12">
                                <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] text-center mb-8">
                                    The Impact of Brotherhood
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                                    <div className="text-center">
                                        <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
                                            <Heart className="w-8 h-8 text-[#C9A227]" />
                                        </div>
                                        <p className="text-3xl font-bold text-[#C9A227] mb-2">100%</p>
                                        <p className="text-[#A0A0A0] text-sm">Feel more connected to community</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
                                            <Users className="w-8 h-8 text-[#C9A227]" />
                                        </div>
                                        <p className="text-3xl font-bold text-[#C9A227] mb-2">Growing</p>
                                        <p className="text-[#A0A0A0] text-sm">Network of brothers across DMV</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
                                            <Quote className="w-8 h-8 text-[#C9A227]" />
                                        </div>
                                        <p className="text-3xl font-bold text-[#C9A227] mb-2">Real</p>
                                        <p className="text-[#A0A0A0] text-sm">Stories of transformation</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Call to Action */}
                    <section>
                        <Card className="bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5 border-[#C9A227]/30">
                            <CardContent className="p-8 lg:p-12 text-center">
                                <h2 className="text-2xl lg:text-3xl font-bold text-white font-['Playfair_Display'] mb-4">
                                    Your Story Could Be Next
                                </h2>
                                <p className="text-[#A0A0A0] max-w-2xl mx-auto mb-8">
                                    Every brother who walks through our doors has a story worth telling. 
                                    Join us and become part of a community that celebrates growth, healing, and authentic connection.
                                </p>
                                <a 
                                    href="/member/register"
                                    className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold px-8 py-3 rounded-lg transition-colors"
                                >
                                    Join the Brotherhood
                                    <Play className="w-4 h-4" />
                                </a>
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </main>

            <MainFooter />
        </div>
    );
};

export default TestimonialsPage;
